/* =====================================================================================
   Expenses.

   WHY THIS IS HERE AND NOT IN EXPENSIFY. Expensify solves a problem this company does not have:
   reconciling thousands of card transactions across many cost centres. What Scira has is a handful
   of claims a month that must be evidenced, approved against a stated authority, and handed to
   payroll. Doing it here means the claim sits next to the thing it was spent on: a dinner with two
   clinicians is already an interaction against a site and a transfer of value under the
   interactions policy, and in a separate system those are three unconnected records with the
   anti-bribery register maintained by hand.

   THIS SCREEN NEVER PAYS ANYBODY. It approves and it exports. Payment stays in payroll, where the
   bank controls already exist and where holding account details is somebody else's job.

   THE POLICY IS CHECKED, NOT DECLARED. There is no "I confirm this is within policy" box. The
   claim is measured against the policy and the exceptions are shown to the approver, because a
   tick box is answered honestly by people who have misremembered the rules.
   ===================================================================================== */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BACKEND } from '../lib/db';
import { TENANT } from '@tenant';
import { downloadCsv, toCsv } from '../lib/resultActions';
import './expenses.css';

interface Claim {
  id: string; reference: string | null; member_id: string; period_month: string;
  status: string; currency: string; submitted_at: string | null;
  decided_at: string | null; decision_note: string | null; self_approved: boolean;
  payment_month: string | null; submitted_late: boolean;
  approver_id: string | null; approver_source: string | null; decided_as: string | null;
}
interface Line {
  id: string; claim_id: string; spent_on: string; category: string; description: string;
  amount: number; alcohol_amount: number; vat_amount: number | null;
  currency: string; amount_base: number | null;
  has_vat_invoice: boolean; attendees: string | null; receipt_path: string | null;
  approver_note: string | null;
}
/* ledger.v_my_expense_approvals: the claims I may decide, with who they were routed to. */
interface Approval {
  id: string; reference: string | null; period_month: string; currency: string;
  submitted_at: string | null; claimant: string; member_id: string; total: number;
  flagged_lines: number; is_own: boolean; approver_id: string | null;
  approver_source: string | null; approver_name: string | null; is_approver: boolean;
}
const ROUTE_LABEL: Record<string, string> = {
  line_manager: 'as line manager', owner: 'as owner', override: 'by assignment',
  fallback: 'as owner, because no line manager is recorded in Crew',
};
interface Flags { line_id: string; claim_id: string; flags: string[] }

const CATEGORIES = [
  ['travel', 'Travel'], ['accommodation', 'Accommodation'], ['subsistence', 'Meals'],
  ['entertainment', 'Client entertainment'], ['mileage', 'Mileage'],
  ['conference', 'Congress or conference'], ['equipment', 'Equipment'],
  ['subscription', 'Subscription'], ['other', 'Other'],
] as const;

/* SCIRA-POL-08. Held here only for what the screen SAYS; what it does is enforced in
   ledger.submit_expense_claim against ledger.expense_thresholds, so a tenant changing
   the number changes the rule rather than only the caption. */
const RECEIPT_MIN = 25;

/* The tenant's book currency. Every default and every threshold below used to be sterling,
   which for a US company meant a dollar taxi fare defaulting to pounds and a receipt rule
   quoted in a currency the company does not use. */
const BASE = TENANT.brand.baseCurrency ?? 'GBP';
const money = (n: number, c = BASE) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: c }).format(n);


/* Shrink and convert before upload, in the browser.

   A phone photograph is three to eight megabytes of HEIC. Claude cannot read HEIC at all, and
   several megabytes of base64 is enough memory pressure to kill an edge worker outright, which
   surfaces to the user as "failed to send a request" because a dead worker returns no CORS
   headers and the browser cannot tell that apart from a network fault.

   Drawing it to a canvas fixes both at once: the browser decodes HEIC even though the API
   cannot, and 1600px on the long edge is more than enough to read a till receipt while being a
   tenth of the bytes. It is also a tenth of the image tokens, so it is cheaper and faster.

   If anything here fails we upload the original and let the function's own guards explain the
   problem. A conversion that silently swallows the file would be worse than the bug. */
async function shrinkForReading(file: File): Promise<File> {
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const MAX = 1600;
    const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale), h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob) return file;
    const base = file.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

export function ExpensesView() {
  const [claims, setClaims] = useState<Claim[] | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [flags, setFlags] = useState<Flags[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [canDecide, setCanDecide] = useState(false);
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [approverNames, setApproverNames] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    spent_on: new Date().toISOString().slice(0, 10), category: 'travel',
    description: '', amount: '', alcohol_amount: '', vat_amount: '',
    has_vat_invoice: false, attendees: '', currency: BASE,
  });
  /* What the receipt reader found, held separately from the form so the claimant can see what
     was read versus what they have since corrected. */
  const [pending, setPending] = useState<{
    path: string; confidence: string; unreadable: string | null; merchant: string | null;
    fx: { rate: number; for_date: string; asked_for: string; fell_back: boolean;
          source: string; amount_base: number } | null;
  } | null>(null);
  // Every currency the book knows, not a guessed shortlist: a receipt arrives in whatever
  // the country used, and filing it mislabelled corrupts the FX audit trail.
  const [ccys, setCcys] = useState<string[]>(
    ['GBP', 'EUR', 'USD', 'CHF', 'SEK', 'DKK', 'NOK', 'PLN', 'AUD', 'CAD', 'JPY', 'AED', 'SGD', 'ZAR']);
  const [reading, setReading] = useState(false);

  const load = useCallback(async () => {
    if (BACKEND !== 'supabase') { setClaims([]); return; }
    const { supabase } = await import('../lib/supabase');
    const app = supabase().schema('app');
    const led = supabase().schema('ledger');
    const [{ data: mid }, { data: c }, { data: l }, { data: f }, { data: a }] = await Promise.all([
      app.rpc('current_member_id'),
      led.from('expense_claims').select('*').order('period_month', { ascending: false }),
      led.from('expense_lines').select('*').order('spent_on'),
      led.from('v_expense_flags').select('*'),
      led.from('v_my_expense_approvals').select('*').order('submitted_at'),
    ]);
    setMe(mid as string | null);
    setClaims((c ?? []) as unknown as Claim[]);
    setLines((l ?? []) as unknown as Line[]);
    setFlags((f ?? []) as unknown as Flags[]);
    setApprovals((a ?? []) as unknown as Approval[]);
    /* Names for "with X for approval" on the claimant's own claims. Members are readable
       within the organisation. */
    const ids = Array.from(new Set(((c ?? []) as Claim[]).map((x) => x.approver_id).filter(Boolean))) as string[];
    if (ids.length) {
      const { data: ms } = await app.from('members').select('id, full_name, display_name').in('id', ids);
      setApproverNames(Object.fromEntries(((ms ?? []) as { id: string; full_name: string; display_name: string | null }[])
        .map((m) => [m.id, m.display_name || m.full_name])));
    }
    const { data: gate } = await app.rpc('may_admin_access');
    setCanDecide(gate === true);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { (async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data } = await supabase().schema('app').from('currencies').select('code').order('code');
      if (data?.length) setCcys(data.map((r) => (r as { code: string }).code));
    } catch { /* the shortlist above still works */ }
  })(); }, []);

  const flagsFor = (lineId: string) =>
    flags.find((f) => f.line_id === lineId)?.flags ?? [];

  const totals = useMemo(() => {
    const t = new Map<string, { sum: number; n: number; flagged: number }>();
    for (const l of lines) {
      const e = t.get(l.claim_id) ?? { sum: 0, n: 0, flagged: 0 };
      e.sum += Number(l.amount_base ?? l.amount); e.n += 1;
      if (flagsFor(l.id).length) e.flagged += 1;
      t.set(l.claim_id, e);
    }
    return t;
  }, [lines, flags]);

  async function call(schema: string, fn: string, args: Record<string, unknown>, ok: string) {
    setBusy(true); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema(schema).rpc(fn, args);
      if (error) throw new Error(error.message);
      setMsg(ok); await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  /* One claim per person per month is enforced by expense_claims_period_uniq. The button used
     to insert blindly and hand the resulting duplicate-key error to the user, who had done
     nothing wrong: they pressed "Start a claim" and this month's claim already existed. It now
     finds that claim instead, which is what pressing the button meant. */
  async function newClaim() {
    setBusy(true); setMsg(null);
    try {
      const month = new Date(); month.setDate(1);
      const period = month.toISOString().slice(0, 10);
      const mine = claims?.find((c) => c.member_id === me
        && c.period_month.slice(0, 7) === period.slice(0, 7));
      if (mine) {
        setOpen(mine.id);
        setMsg(mine.status === 'draft'
          ? 'Your claim for this month is already open below.'
          : 'This month\u2019s claim has already been submitted. Expenses are one claim a month.');
        return;
      }
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('ledger').from('expense_claims')
        .insert({ member_id: me, period_month: period, currency: BASE });
      if (error) throw new Error(error.message);
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  /* A receipt is a photograph taken on a phone, so the bucket accepts images where the registry
     bucket refuses them. Foldered org/member so an approver can reach a claimant's receipts and a
     colleague cannot reach anybody's. */
  async function attachReceipt(line: Line, file: File) {
    setBusy(true); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const app = supabase().schema('app');
      const [{ data: org }, { data: mem }] = await Promise.all([
        app.rpc('current_org_id'), app.rpc('current_member_id'),
      ]);
      const path = `${org}/${mem}/${line.id}-${file.name.replace(/[^\w.\-]+/g, '_')}`;
      const { error: upErr } = await supabase().storage.from('receipts')
        .upload(path, file, { upsert: true, contentType: file.type || undefined });
      if (upErr) throw new Error(upErr.message);
      const { error } = await supabase().schema('ledger').from('expense_lines')
        .update({ receipt_path: path }).eq('id', line.id);
      if (error) throw new Error(`Uploaded, but not recorded: ${error.message}`);
      setMsg('Receipt attached.');
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  async function viewReceipt(line: Line) {
    if (!line.receipt_path) return;
    /* Open the tab SYNCHRONOUSLY, inside the click, then point it at the signed URL when that
       arrives. window.open after an awaited network call has lost the user gesture and is
       blocked by the popup blocker in every mainstream browser, so the button appeared to do
       nothing: found by driving the button in a headless browser and watching no tab appear. */
    const tab = window.open('', '_blank');
    try {
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase().storage.from('receipts')
        .createSignedUrl(line.receipt_path, 120);
      if (error) throw new Error(error.message);
      if (tab) tab.location.href = data.signedUrl; else window.location.assign(data.signedUrl);
    } catch (e) {
      tab?.close();
      setMsg(String((e as Error).message));
    }
  }


  /* Upload, read, prefill. The line is NOT created here: the claimant sees every field the
     receipt produced and presses Add, so a misread costs one correction rather than a wrong
     claim. Extraction proposes; a person decides. */
  async function readReceipt(file: File) {
    setReading(true); setMsg(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const sb = supabase();
      const app = sb.schema('app');
      const [{ data: org }, { data: mem }] = await Promise.all([
        app.rpc('current_org_id'), app.rpc('current_member_id'),
      ]);
      const shrunk = await shrinkForReading(file);
      const path = `${org}/${mem}/${Date.now()}-${shrunk.name.replace(/[^\w.\-]+/g, '_')}`;
      const up = await sb.storage.from('receipts')
        .upload(path, shrunk, { contentType: shrunk.type || undefined, upsert: false });
      if (up.error) throw new Error(up.error.message);

      const { data, error } = await sb.functions.invoke('read-receipt',
        { body: { path, base: BASE } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setPending({
        path, confidence: data.confidence ?? 'partial', unreadable: data.unreadable ?? null,
        merchant: data.merchant ?? null, fx: data.fx ?? null,
      });
      setDraft((d) => ({
        ...d,
        spent_on: data.spent_on || d.spent_on,
        category: data.category || d.category,
        description: data.description || d.description,
        amount: data.amount != null ? String(data.amount) : d.amount,
        currency: data.currency || BASE,
        alcohol_amount: data.alcohol_amount ? String(data.alcohol_amount) : '',
        vat_amount: data.vat_amount != null ? String(data.vat_amount) : '',
        has_vat_invoice: !!data.has_vat_invoice,
        attendees: data.attendees || d.attendees,
      }));
    } catch (e) {
      const m = (e as Error).message;
      setMsg(/failed to send|fetch/i.test(m)
        ? 'The reader did not respond. That is usually a very large image or a format it cannot '
          + 'open. Try a photograph rather than a scan, or type the line in and attach the file '
          + 'afterwards, which always works.'
        : `Could not read that receipt: ${m}. You can still type the line in and attach the `
          + 'image afterwards.');
    } finally { setReading(false); }
  }

  async function addLine(claimId: string) {
    if (!draft.description.trim() || !draft.amount) { setMsg('A description and an amount.'); return; }
    setBusy(true);
    try {
      /* A typed-in foreign line has no rate yet (the reader only prices receipts it has seen).
         The book needs one: fetch the ECB reference rate for the spend date now, so the rate
         travels with the line exactly as it does on a read receipt. */
      let fx = pending?.fx && draft.currency !== BASE ? pending.fx : null;
      if (!fx && draft.currency !== BASE) {
        try {
          const r = await fetch(`https://api.frankfurter.dev/v1/${draft.spent_on}?base=${draft.currency}&symbols=${BASE}`);
          const d = r.ok ? await r.json() : null;
          const rate = d?.rates?.[BASE];
          if (rate) fx = { rate, for_date: d.date, asked_for: draft.spent_on, fell_back: d.date !== draft.spent_on,
                           source: 'European Central Bank reference rate via frankfurter.dev',
                           amount_base: Math.round(Number(draft.amount) * rate * 100) / 100 };
        } catch { /* handled below */ }
        if (!fx) {
          setMsg(`No published exchange rate for ${draft.currency} on that date. The ECB reference `
               + `series does not cover every currency: either enter the ${BASE} value from your bank `
               + `statement as a ${BASE} line and say so in the description, or pick a covered currency.`);
          setBusy(false); return;
        }
      }
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase().schema('ledger').from('expense_lines').insert({
        claim_id: claimId, spent_on: draft.spent_on, category: draft.category,
        description: draft.description.trim(), amount: Number(draft.amount),
        alcohol_amount: Number(draft.alcohol_amount || 0),
        vat_amount: draft.vat_amount ? Number(draft.vat_amount) : null,
        has_vat_invoice: draft.has_vat_invoice,
        attendees: draft.attendees.trim() || null,
        currency: draft.currency || BASE,
        // The rate travels with the line. Looking it up again at submission time would give a
        // different answer, and a claim that revalues itself cannot be reconciled.
        ...(fx ? { fx_rate: fx.rate, fx_date: fx.for_date, fx_source: fx.source } : {}),
        // Attached at creation, so a line that needed a receipt never exists without one.
        receipt_path: pending?.path ?? null,
      });
      if (error) throw new Error(error.message);
      setPending(null);
      setDraft((d) => ({ ...d, description: '', amount: '', alcohol_amount: '',
                         vat_amount: '', attendees: '', currency: BASE }));
      await load();
    } catch (e) { setMsg(String((e as Error).message)); }
    finally { setBusy(false); }
  }

  if (!claims) return <p className="muted">Loading</p>;

  const mine = claims.filter((c) => c.member_id === me);

  return (
    <div className="page">
      <div className="page-head">
        <h1 className="page-title">Expenses</h1>
        <p className="page-sub">
          Claim, approve, and export to payroll. {TENANT.brand.productName} never pays anybody:
          payment stays where the bank controls are.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {approvals.length > 0 && (
        <section className="card">
          <h2>Awaiting your decision</h2>
          <p className="muted exp-route-note">
            A claim comes to its claimant's line manager, from Crew; the owner's and the board's
            come to the owner. Open it, look at every line and receipt, then approve it or return
            it with what needs amending.
          </p>
          {approvals.map((a) => {
            const cl = lines.filter((l) => l.claim_id === a.id);
            const isOpen = open === a.id;
            return (
              <div key={a.id} className="exp-claim exp-approval">
                <button className="exp-claim-head" onClick={() => setOpen(isOpen ? null : a.id)}>
                  <strong>{a.reference ?? 'claim'}</strong>
                  <span className="muted">{a.period_month?.slice(0, 7)}</span>
                  <span>{a.claimant}</span>
                  <span className="muted">
                    {money(Number(a.total), a.currency)} over {cl.length} lines
                    {!!a.flagged_lines && <span className="exp-flagcount"> · {a.flagged_lines} needing a look</span>}
                  </span>
                  <span className="exp-route">
                    {a.is_approver
                      ? `with you ${ROUTE_LABEL[a.approver_source ?? ''] ?? ''}`
                      : `with ${a.approver_name ?? 'nobody'}; deciding it here is recorded as a finance override`}
                    {a.is_own && ' · your own claim, recorded as self-approved'}
                  </span>
                  <span className="exp-total">{isOpen ? 'Close' : 'Open'}</span>
                </button>
                {isOpen && (
                  <div className="exp-body">
                    <table className="data">
                      <thead>
                        <tr><th>Date</th><th>What</th><th className="num">Amount</th><th>Receipt</th><th>Your query</th></tr>
                      </thead>
                      <tbody>
                        {cl.map((l) => {
                          const f = flagsFor(l.id);
                          return (
                            <tr key={l.id}>
                              <td>{l.spent_on}</td>
                              <td>
                                <div className="cell-title">{l.description}</div>
                                <div className="cell-sub">
                                  {CATEGORIES.find((x) => x[0] === l.category)?.[1] ?? l.category}
                                  {l.attendees && <> · {l.attendees}</>}
                                </div>
                                {f.map((x, i) => <div key={i} className="exp-flag">{x}</div>)}
                              </td>
                              <td className="num">
                                {money(Number(l.amount), l.currency || a.currency)}
                                {l.currency && l.currency !== a.currency && l.amount_base != null && (
                                  <div className="cell-sub">= {money(Number(l.amount_base), a.currency)}</div>
                                )}
                                {Number(l.alcohol_amount) > 0 && (
                                  <div className="cell-sub">incl. {money(Number(l.alcohol_amount), l.currency || a.currency)} alcohol</div>
                                )}
                              </td>
                              <td>
                                {l.receipt_path
                                  ? <button className="link-btn" onClick={() => viewReceipt(l)}>open receipt</button>
                                  : <span className="muted">no receipt</span>}
                              </td>
                              <td>
                                <button className="link-btn" disabled={busy} onClick={() => {
                                  const q = window.prompt('Your query on this line. The claimant sees it when the claim is returned. Leave empty to clear it.', l.approver_note ?? '');
                                  if (q === null) return;
                                  call('ledger', 'query_expense_line', { p_line: l.id, p_note: q }, q.trim() ? 'Query recorded on the line.' : 'Query cleared.');
                                }}>
                                  {l.approver_note ? 'edit query' : 'query'}
                                </button>
                                {l.approver_note && <div className="exp-query">{l.approver_note}</div>}
                              </td>
                            </tr>
                          );
                        })}
                        {cl.length === 0 && (
                          <tr><td colSpan={5} className="muted">The lines are not visible to you.</td></tr>
                        )}
                      </tbody>
                    </table>
                    <div className="exp-decide-actions exp-decide-foot">
                      <button className="btn primary" disabled={busy}
                              onClick={() => call('ledger', 'decide_expense_claim',
                                                  { p_claim: a.id, p_approve: true }, 'Approved.')}>
                        Approve
                      </button>
                      <button className="btn" disabled={busy}
                              onClick={() => {
                                const why = window.prompt('What needs amending? The claimant sees this with any '
                                                          + 'line queries and can then resubmit.');
                                if (why === null) return;
                                call('ledger', 'decide_expense_claim',
                                     { p_claim: a.id, p_approve: false, p_note: why.trim() },
                                     'Returned for amendment; the claimant has been told.');
                              }}>
                        Return for amendment
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      <section className="card">
        <div className="exp-head">
          <h2>Your claims</h2>
          <div className="exp-head-actions">
            {canDecide && (
              <button className="btn" onClick={async () => {
                const { supabase } = await import('../lib/supabase');
                const { data } = await supabase().schema('ledger')
                  .from('v_expense_payroll_export').select('*');
                const rows = (data ?? []) as unknown as Record<string, unknown>[];
                if (!rows.length) { setMsg('Nothing approved to export.'); return; }
                downloadCsv(`payroll-expenses-${new Date().toISOString().slice(0, 10)}`,
                  toCsv([{ key: 'reference', label: 'Reference' },
                         { key: 'full_name', label: 'Name' },
                         { key: 'period_month', label: 'Period' },
                         { key: 'gross', label: 'Gross' },
                         { key: 'vat', label: 'VAT' },
                         { key: 'mileage', label: 'Mileage' },
                         { key: 'approved_on', label: 'Approved' }], rows));
              }}>Export approved to payroll</button>
            )}
            <button className="btn primary" onClick={newClaim} disabled={busy}>
              Start a claim
            </button>
          </div>
        </div>

        {mine.length === 0 && <p className="muted">No claims yet.</p>}

        {mine.map((c) => {
          const t = totals.get(c.id);
          const cl = lines.filter((l) => l.claim_id === c.id);
          const editable = c.status === 'draft' || c.status === 'rejected';
          return (
            <div key={c.id} className="exp-claim">
              <button className="exp-claim-head" onClick={() => setOpen(open === c.id ? null : c.id)}>
                <span className={`exp-status st-${c.status}`}>{c.status}</span>
                {c.self_approved && (
                  <span className="exp-selftag" title="Approved by the claimant. Shown here and in
                                                       the payroll export, and listed in the
                                                       self-approved register.">self-approved</span>
                )}
                <strong>{c.reference ?? 'Draft'}</strong>
                <span className="muted">{c.period_month?.slice(0, 7)}</span>
                <span className="exp-total">{money(t?.sum ?? 0, c.currency)}</span>
              </button>

              {c.status === 'submitted' && c.approver_id && (
                <p className="exp-with muted">
                  With {c.approver_id === me ? 'you' : (approverNames[c.approver_id] ?? 'your approver')} for
                  approval {ROUTE_LABEL[c.approver_source ?? ''] ?? ''}.
                </p>
              )}
              {c.status === 'rejected' && (
                <p className="exp-rejected"><strong>Returned for amendment.</strong> {c.decision_note
                  ?? 'See the queries on the lines below.'} Amend it and submit again.</p>
              )}
              {c.status === 'approved' && c.decided_as === 'finance_override' && (
                <p className="exp-with muted">Approved by finance in place of the assigned approver.</p>
              )}

              {open === c.id && (
                <div className="exp-body">
                  <table className="data">
                    <thead>
                      <tr><th>Date</th><th>What</th><th className="num">Amount</th><th>Notes</th></tr>
                    </thead>
                    <tbody>
                      {cl.map((l) => {
                        const f = flagsFor(l.id);
                        return (
                          <tr key={l.id}>
                            <td>{l.spent_on}</td>
                            <td>
                              <div className="cell-title">{l.description}</div>
                              <div className="cell-sub">
                                {CATEGORIES.find((x) => x[0] === l.category)?.[1] ?? l.category}
                                {l.attendees && <> · {l.attendees}</>}
                              </div>
                            </td>
                            <td className="num">
                              {money(Number(l.amount), l.currency || c.currency)}
                              {l.currency && l.currency !== c.currency && l.amount_base != null && (
                                <div className="cell-sub">= {money(Number(l.amount_base), c.currency)}</div>
                              )}
                              {Number(l.alcohol_amount) > 0 && (
                                <div className="cell-sub">
                                  incl. {money(Number(l.alcohol_amount), l.currency || c.currency)} alcohol
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="exp-receipt">
                                {l.receipt_path ? (
                                  <button className="link-btn" onClick={() => viewReceipt(l)}>
                                    receipt
                                  </button>
                                ) : (
                                  <span className="muted">no receipt</span>
                                )}
                                {editable && (
                                  <label className="link-btn exp-attach">
                                    {l.receipt_path ? 'replace' : 'attach'}
                                    <input type="file" hidden
                                           accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
                                           onChange={(e) => {
                                             const file = e.target.files?.[0];
                                             if (file) attachReceipt(l, file);
                                             e.target.value = '';
                                           }} />
                                  </label>
                                )}
                              </div>
                              {f.map((x, i) => (
                                <div key={i} className="exp-flag">{x}</div>
                              ))}
                              {/* The approver's query, while it is still live: on a returned claim
                                  it is what to fix, on a submitted one what is being asked. Once
                                  decided it is history and stays in the record, not on the screen. */}
                              {l.approver_note && (c.status === 'rejected' || c.status === 'submitted') && (
                                <div className="exp-query"><strong>Query:</strong> {l.approver_note}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {cl.length === 0 && (
                        <tr><td colSpan={4} className="muted">Nothing on this claim yet.</td></tr>
                      )}
                    </tbody>
                  </table>

                  {editable && (
                    <div className="exp-add">
                      {/* Receipt first, form second. Photographing the receipt is the thing the
                          claimant actually has in their hand; making them type the fields and
                          THEN find the attach button gets the order backwards, and it is why
                          receipts go missing. */}
                      <div className="exp-shot">
                        <label className={`exp-shot-btn${reading ? ' busy' : ''}`}>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*,application/pdf"
                                 disabled={reading || busy}
                                 onChange={(e) => {
                                   const f = e.target.files?.[0];
                                   if (f) readReceipt(f);
                                   e.target.value = '';
                                 }} />
                          <span className="exp-shot-main">
                            {reading ? 'Reading the receipt\u2026'
                                     : pending ? 'Use a different receipt'
                                     : 'Add a receipt'}
                          </span>
                          {!reading && !pending && (
                            <span className="exp-shot-sub">
                              Photograph it, pick one from your photos, or choose a scan or PDF
                            </span>
                          )}
                        </label>
                        {pending && (
                          <p className={`exp-read c-${pending.confidence}`}>
                            {pending.merchant && <strong>{pending.merchant}. </strong>}
                            {pending.confidence === 'poor'
                              ? 'The image was hard to read. Check every field below before adding it.'
                              : pending.confidence === 'partial'
                                ? 'Read, but not all of it. Check the fields below.'
                                : 'Read. Check it and add it.'}
                            {pending.unreadable && <> Could not read: {pending.unreadable}.</>}
                            {pending.fx && (
                              <> {draft.currency} at {pending.fx.rate} on {pending.fx.for_date}
                                {pending.fx.fell_back && <> (no rate published {pending.fx.asked_for},
                                  so the preceding working day)</>}
                                {' '}gives about {money(pending.fx.amount_base)}. ECB reference rate.</>
                            )}
                          </p>
                        )}
                      </div>
                      <input className="input" type="date" value={draft.spent_on}
                             onChange={(e) => setDraft({ ...draft, spent_on: e.target.value })} />
                      <select className="input sel" value={draft.category}
                              onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                        {CATEGORIES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <input className="input grow" placeholder="What was it for, in one line"
                             value={draft.description}
                             onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                      {/* Currency beside the amount rather than hidden in a setting: a euro
                          receipt filed as sterling short-changes the claimant silently, and the
                          only moment anybody knows the currency is while looking at the receipt. */}
                      <select className="input ccy" value={draft.currency} disabled={busy}
                              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                        {[BASE, ...ccys.filter((c) => c !== BASE)]
                          .map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input className="input narrow" type="number" step="0.01" placeholder="Amount"
                             value={draft.amount}
                             onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
                      <input className="input narrow" type="number" step="0.01" placeholder="of which alcohol"
                             value={draft.alcohol_amount}
                             onChange={(e) => setDraft({ ...draft, alcohol_amount: e.target.value })} />
                      <input className="input narrow" type="number" step="0.01" placeholder="VAT"
                             value={draft.vat_amount}
                             onChange={(e) => setDraft({ ...draft, vat_amount: e.target.value })} />
                      <label className="exp-check">
                        <input type="checkbox" checked={draft.has_vat_invoice}
                               onChange={(e) => setDraft({ ...draft, has_vat_invoice: e.target.checked })} />
                        VAT invoice
                      </label>
                      <input className="input grow" placeholder="Who else was there, and where they work"
                             value={draft.attendees}
                             onChange={(e) => setDraft({ ...draft, attendees: e.target.value })} />
                      <button className="btn" disabled={busy} onClick={() => addLine(c.id)}>Add</button>
                    </div>
                  )}

                  {editable && cl.length > 0 && (() => {
                    /* Named here rather than discovered on refusal. The rule is real: submission
                       and approval both reject a claim with an unevidenced line at or above the
                       threshold, so showing it late would just be a slower way to say no. */
                    const owing = cl.filter((l) => Number(l.amount) >= RECEIPT_MIN
                                                && !l.receipt_path);
                    return (
                    <div className="exp-submit">
                      {owing.length > 0 && (
                        <p className="exp-owing">
                          {owing.length === 1 ? 'One line needs' : `${owing.length} lines need`} a
                          receipt before this can be submitted: {owing.map((l) => l.description)
                            .join('; ')}. Required at {money(RECEIPT_MIN)} and above.
                        </p>
                      )}
                      <button className="btn primary" disabled={busy || owing.length > 0}
                              title={owing.length > 0
                                ? 'Attach the missing receipts first' : undefined}
                              onClick={() => call('ledger', 'submit_expense_claim',
                                                  { p_claim: c.id }, 'Submitted.')}>
                        Submit this claim
                      </button>
                      {/* The deadline said before it is missed, not after. */}
                      <span className="muted">
                        {(() => {
                          const per = new Date(c.period_month);
                          const dl = new Date(per.getFullYear(), per.getMonth() + 1, 5);
                          const late = new Date() > dl;
                          return `Due by ${dl.toLocaleDateString('en-GB',
                            { day: 'numeric', month: 'long' })}. `
                            + (late
                               ? 'That has passed, so this will reach the following payroll run.'
                               : 'Submitted by then it is paid in that month\u2019s run.') + ' ';
                        })()}
                      </span>
                      <span className="muted">
                        {t?.flagged
                          ? `${t.flagged} line${t.flagged === 1 ? '' : 's'} depart from the policy. `
                            + 'You can still submit; the approver sees them.'
                          : 'Everything on this claim is within policy.'}
                      </span>
                    </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </section>
    </div>
  );
}
